import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useSelector } from 'react-redux'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Legend,
  ArcElement,
  PointElement,
} from 'chart.js'
import {
  CalculateActionProgressBySource,
  CalculateProjectBudgetPercentage,
  countActionsPriorityByProjetId,
  countActionsStatusByProjetId,
  CountSecurityCheckFamilyByProject,
  fetchSCFCounts,
  getActionCountsBySourceCategory,
  getactionCountsByStatus,
  getactionCountsByTypeAndStatus,
  getActionPriorityCriticalRatio,
  getActionPriorityHighRatio,
  getActionPriorityLowRatio,
  getActionPriorityMediumRatio,
  GetActionsBudgetTable,
  getActionStatusCoursRatio,
  getActionStatusDoneRatio,
  getOverdueInProgressActionRatio,
  getOverdueInProgressCriticalHighActionRatio,
  getProjetbyId,
} from '../../redux/projets/projetActions'
import { analysisColorPalette } from '../../utils/colors'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'
import PieChart, { hasChartData } from '../../components/common/PieChart'
import BarChart from '../../components/common/BarChart'
import MatrixComponent from '../../components/common/MatrixTable'
import LineChart from '../../components/common/LineChart'
import { selectSCFCounts } from '../../redux/projets/projetSelectors'
import AnalysisPdfReport from '../../Pdf/AnalysisPdfReport'
import {
  AnalysisPdfButton,
  useAnalysisPdfExport,
} from '../../components/common/AnalysisPdfButton'
import { formatCurrency, locale, formatDecimal } from '../../utils/formatNumbers'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import {
  AnalysisChartCard,
  AnalysisKpiCard,
  AnalysisPageLoader,
  AnalysisSection,
  pctTone,
} from '../../components/common/analysisLayout'
import {
  DetailButton,
  DetailPageHead,
} from '../../components/common/detail'
import '../../components/common/maturity-analysis.css'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Legend,
  ArcElement
)

const COLORS = analysisColorPalette

const ProjectIndicatorsPage = () => {
  const dispatch: AppDispatch = useAppDispatch()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const { ProjetId } = useParams<{ ProjetId: string }>()
  const [loading, setLoading] = useState(true)
  const scfCounts = useSelector(selectSCFCounts)
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!ProjetId) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        await dispatch(getProjetbyId(ProjetId))
        await dispatch(getActionStatusCoursRatio({ idProjet: ProjetId }))
        await dispatch(getActionPriorityHighRatio({ idProjet: ProjetId }))
        await dispatch(getActionPriorityCriticalRatio({ idProjet: ProjetId }))
        await dispatch(getActionPriorityLowRatio({ idProjet: ProjetId }))
        await dispatch(getActionStatusDoneRatio({ idProjet: ProjetId }))
        await dispatch(getOverdueInProgressActionRatio({ idProjet: ProjetId }))
        await dispatch(
          getOverdueInProgressCriticalHighActionRatio({ idProjet: ProjetId })
        )
        await dispatch(getActionPriorityMediumRatio({ idProjet: ProjetId }))
        await dispatch(countActionsStatusByProjetId({ idProjet: ProjetId }))
        await dispatch(countActionsPriorityByProjetId({ idProjet: ProjetId }))
        await dispatch(getActionCountsBySourceCategory({ idProjet: ProjetId }))
        await dispatch(getactionCountsByStatus({ idProjet: ProjetId }))
        await dispatch(getactionCountsByTypeAndStatus({ idProjet: ProjetId }))
        await dispatch(CalculateProjectBudgetPercentage({ idProjet: ProjetId }))
        await dispatch(GetActionsBudgetTable({ idProjet: ProjetId }))
        await dispatch(CalculateActionProgressBySource({ idProjet: ProjetId }))
        await dispatch(
          CountSecurityCheckFamilyByProject({ idProjet: ProjetId })
        )
        dispatch(fetchSCFCounts(ProjetId))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [ProjetId, dispatch])

  const ActionProgressBySource = useSelector(
    (state: RootState) => state.projet.calculateActionProgressBySource
  )

  const OverdueInProgressActionRatio = useSelector(
    (state: RootState) => state.projet.InProgressActionRatio?.ratio
  )

  const countSecurityCheckFamily = useSelector(
    (state: RootState) => state.projet.countSecurityCheckFamilyByProject
  )

  const BudgetTable = useSelector(
    (state: RootState) => state.projet.getActionsBudgetTable
  )

  const ActionsPriority = useSelector(
    (state: RootState) => state.projet.ProjetPriority?.counts
  )

  const OverdueInProgressCriticalHighActionRatio = useSelector(
    (state: RootState) => state.projet.InProgressCriticalHighActionRatio?.ratio
  )

  const CountsBySourceCategory = useSelector(
    (state: RootState) => state.projet.ActionsBySourceCategory?.counts
  )

  const ProjectBudgetPercentage = useSelector(
    (state: RootState) => state.projet.percentageBudget?.budgetPercentage
  )

  const actionCountsByStatus = useSelector(
    (state: RootState) => state.projet.ActionCountsByStatus?.counts
  )

  const actionCountsByTypeAndStatus = useSelector(
    (state: RootState) => state.projet.ActionCountsByStatusAndType
  )

  const tasksByStatusData = {
    legend: {
      position: 'bottom' as const,
    },
    labels: [
      t('charts.taskStatus.statuses.todo'),
      t('charts.taskStatus.statuses.inProgress'),
      t('charts.taskStatus.statuses.suspended'),
      t('charts.taskStatus.statuses.done'),
      t('charts.taskStatus.statuses.waiting'),
    ],
    datasets: [
      {
        data: [
          actionCountsByStatus?.['A faire'] ?? 0,
          actionCountsByStatus?.['En cours'] ?? 0,
          actionCountsByStatus?.Suspendu ?? 0,
          actionCountsByStatus?.Terminé ?? 0,
          actionCountsByStatus?.['En attente'] ?? 0,
        ],
        backgroundColor: COLORS.slice(0, 5),
        hoverBackgroundColor: COLORS.slice(0, 5),
        hoverOffset: 4,
      },
    ],
  }

  const tasksByPriorityData = {
    labels: [
      t('charts.taskPriority.priorities.low'),
      t('charts.taskPriority.priorities.medium'),
      t('charts.taskPriority.priorities.high'),
      t('charts.taskPriority.priorities.critical'),
    ],
    datasets: [
      {
        data: [
          ActionsPriority?.Low ?? 0,
          ActionsPriority?.Medium ?? 0,
          ActionsPriority?.High ?? 0,
          ActionsPriority?.Critical ?? 0,
        ],
        backgroundColor: COLORS.slice(4, 8),
        hoverBackgroundColor: COLORS.slice(4, 8),
        hoverOffset: 4,
      },
    ],
  }

  const actionsBySourceData = {
    labels: [
      t('charts.actions.sources.audit'),
      t('charts.actions.sources.incident'),
      t('charts.actions.sources.risk'),
    ],
    datasets: [
      {
        label: t('charts.actions.datasets.audit'),
        data: [CountsBySourceCategory?.audit ?? 0, 0, 0],
        backgroundColor: [COLORS[0], COLORS[3], COLORS[2]],
        hoverBackgroundColor: [COLORS[0], COLORS[4], 'transparent'],
        borderColor: [COLORS[0], COLORS[4], 'transparent'],
        borderWidth: 1,
      },
      {
        label: t('charts.actions.datasets.incident'),
        data: [0, CountsBySourceCategory?.incident ?? 0, 0],
        backgroundColor: ['transparent', COLORS[3], 'transparent'],
        hoverBackgroundColor: ['transparent', COLORS[3], 'transparent'],
        borderColor: ['transparent', COLORS[3], 'transparent'],
        borderWidth: 1,
      },
      {
        label: t('charts.actions.datasets.risk'),
        data: [0, 0, CountsBySourceCategory?.risque ?? 0],
        backgroundColor: ['transparent', 'transparent', COLORS[2]],
        hoverBackgroundColor: ['transparent', 'transparent', COLORS[2]],
        borderColor: ['transparent', 'transparent', COLORS[2]],
        borderWidth: 1,
      },
    ],
  }

  const overduePercentage = OverdueInProgressActionRatio
  const highPriorityRate = OverdueInProgressCriticalHighActionRatio

  const actionsByTypeData = {
    labels: [
      t('charts.actions.types.IT'),
      t('charts.actions.types.gouvernance'),
      t('charts.actions.types.physique'),
      t('charts.actions.types.operationnel'),
      t('charts.actions.types.organisationnel'),
    ],
    datasets: [
      {
        label: t('charts.actions.statuses.todo'),
        data: [
          actionCountsByTypeAndStatus?.counts?.IT['A faire'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Gouvernance['A faire'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Physique['A faire'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Opérationnel['A faire'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Organisationnel['A faire'] ?? 0,
        ],
        backgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
          COLORS[4],
        ],
        borderColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
          COLORS[4],
        ],
        borderWidth: 1,
      },
      {
        label: t('charts.actions.statuses.inProgress'),
        data: [
          actionCountsByTypeAndStatus?.counts?.IT['En cours'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Gouvernance['En cours'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Physique['En cours'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Opérationnel['En cours'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Organisationnel['En cours'] ?? 0,
        ],
        backgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
          COLORS[4],
        ],
        borderColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
          COLORS[4],
        ],
        borderWidth: 1,
      },
      {
        label: t('charts.actions.statuses.suspended'),
        data: [
          actionCountsByTypeAndStatus?.counts?.IT.Suspendu ?? 0,
          actionCountsByTypeAndStatus?.counts?.Gouvernance.Suspendu ?? 0,
          actionCountsByTypeAndStatus?.counts?.Physique.Suspendu ?? 0,
          actionCountsByTypeAndStatus?.counts?.Opérationnel.Suspendu ?? 0,
          actionCountsByTypeAndStatus?.counts?.Organisationnel.Suspendu ?? 0,
        ],
        backgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
          COLORS[4],
        ],
        borderColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
          COLORS[4],
        ],
        borderWidth: 1,
      },
      {
        label: t('charts.actions.statuses.done'),
        data: [
          actionCountsByTypeAndStatus?.counts?.IT.Terminé ?? 0,
          actionCountsByTypeAndStatus?.counts?.Gouvernance.Terminé ?? 0,
          actionCountsByTypeAndStatus?.counts?.Physique.Terminé ?? 0,
          actionCountsByTypeAndStatus?.counts?.Opérationnel.Terminé ?? 0,
          actionCountsByTypeAndStatus?.counts?.Organisationnel.Terminé ?? 0,
        ],
        backgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
          COLORS[4],
        ],
        borderColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
          COLORS[4],
        ],
        borderWidth: 1,
      },
      {
        label: t('charts.actions.statuses.waiting'),
        data: [
          actionCountsByTypeAndStatus?.counts?.IT['En attente'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Gouvernance['En attente'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Physique['En attente'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Opérationnel['En attente'] ?? 0,
          actionCountsByTypeAndStatus?.counts?.Organisationnel['En attente'] ??
            0,
        ],
        backgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
          COLORS[4],
        ],
        borderColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
          COLORS[4],
        ],
        borderWidth: 1,
      },
    ],
  }
  const CountSecurityCheckFamily = countSecurityCheckFamily?.counts || {}
  const labels = Object.keys(CountSecurityCheckFamily)
  const dataValues = Object.values(CountSecurityCheckFamily).map(Number)
  const SecurityData = {
    labels,
    datasets: [
      {
        label: t('charts.securityCheck.label'),
        data: dataValues,
        backgroundColor: labels.map((_, i) => COLORS[i % COLORS.length]),
        borderColor: labels.map((_, i) => COLORS[i % COLORS.length]),
        borderWidth: 1,
      },
    ],
  }

  const ProjectBudget: number = ProjectBudgetPercentage ?? 0
  const MissionBudgetPercentage: number = 100 - ProjectBudget

  const budgetComparisonData = {
    labels: [
      t('charts.budget.labels.project'),
      t('charts.budget.labels.mission'),
    ],
    datasets: [
      {
        data: [ProjectBudget, MissionBudgetPercentage],
        backgroundColor: [COLORS[5], COLORS[6]],
        hoverBackgroundColor: [COLORS[7], COLORS[8]],
        hoverOffset: 4,
      },
    ],
  }

  const sources = ['SourceRisque', 'SourceIncident', 'SourceAudit'] as const

  const sourceColors: Record<(typeof sources)[number], string> = {
    SourceRisque: COLORS[4],
    SourceIncident: COLORS[5],
    SourceAudit: COLORS[6],
  }

  const rates = ActionProgressBySource?.rates as Record<
    string,
    Record<string, number>
  >

  const stackedBarChartData = {
    labels: sources.map((source) =>
      t(`charts.actionProgressBySource.sources.${source}`)
    ),
    datasets: [
      {
        label: t('charts.actionProgressBySource.datasets.todo'),
        data: sources.map((source) => rates?.[source]?.['A faire'] ?? 0),
        backgroundColor: sources.map((source) => sourceColors[source]),
        borderColor: sources.map((source) => sourceColors[source]),
        borderWidth: 1,
      },
      {
        label: t('charts.actionProgressBySource.datasets.inProgress'),
        data: sources.map((source) => rates?.[source]?.['En cours'] ?? 0),
        backgroundColor: sources.map((source) => sourceColors[source]),
        borderColor: sources.map((source) => sourceColors[source]),
        borderWidth: 1,
      },
      {
        label: t('charts.actionProgressBySource.datasets.suspended'),
        data: sources.map((source) => rates?.[source]?.Suspendu ?? 0),
        backgroundColor: sources.map((source) => sourceColors[source]),
        borderColor: sources.map((source) => sourceColors[source]),
        borderWidth: 1,
      },
      {
        label: t('charts.actionProgressBySource.datasets.done'),
        data: sources.map((source) => rates?.[source]?.Terminé ?? 0),
        backgroundColor: sources.map((source) => sourceColors[source]),
        borderColor: sources.map((source) => sourceColors[source]),
        borderWidth: 1,
      },
      {
        label: t('charts.actionProgressBySource.datasets.waiting'),
        data: sources.map((source) => rates?.[source]?.['En attente'] ?? 0),
        backgroundColor: sources.map((source) => sourceColors[source]),
        borderColor: sources.map((source) => sourceColors[source]),
        borderWidth: 1,
      },
    ],
  }

  const projet = useSelector((state: RootState) => state.projet.projet)
  const currency = useSelector(selectSpecifications)[0]?.Currency

  const TaskBudget =
    BudgetTable?.table?.map((item) => ({
      name: item.name,
      budget: `${formatCurrency(item.budget ?? 0, currency, {
        locale,
        useIntl: true,
      })}`,
    })) || []

  const headers = [
    t('charts.taskBudgetMatrix.headers.projectName'),
    t('charts.taskBudgetMatrix.headers.budget'),
  ]

  const formattedData = TaskBudget.map((action) => ({
    [t('charts.taskBudgetMatrix.headers.projectName')]: action.name,
    [t('charts.taskBudgetMatrix.headers.budget')]: action.budget,
  }))

  const chartData = {
    labels: scfCounts?.scfCounts.map((scf) => scf.label) || [],
    datasets: [
      {
        label: t('charts.scfChart.datasetLabel'),
        data: scfCounts?.scfCounts.map((scf) => scf.count) || [],
        borderColor: COLORS[0],
        backgroundColor: 'rgba(0, 20, 109, 0.15)',
      },
    ],
  }

  const pdf = useAnalysisPdfExport()

  const computedOn = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [i18n.language]
  )

  const overduePct = Math.min(
    100,
    Math.max(0, Number(overduePercentage) * 100 || 0)
  )
  const highPriorityPct = Math.min(
    100,
    Math.max(0, Number(highPriorityRate) * 100 || 0)
  )
  const budgetPct = Math.min(
    100,
    Math.max(0, Number(ProjectBudgetPercentage) || 0)
  )

  const stampTitle = t('analysis.projectAnalysis.computedOn', {
    date: computedOn,
  })
  const backPath = ProjetId ? `/projet/${ProjetId}` : '/projets'

  return (
    <div className="maturity-analysis-page detail-page">
      <DetailPageHead
        eyebrow={t('analysis.projectAnalysis.eyebrow')}
        title={t('analysis.projectAnalysis.pageTitle')}
        subtitle={t('analysis.projectAnalysis.subtitle', {
          name: projet?.ProjetName || '—',
          date: computedOn,
        })}
        actions={
          <>
            <DetailButton variant="ghost" onClick={() => navigate(backPath)}>
              <ArrowBackOutlined sx={{ fontSize: 16 }} />
              {t('analysis.projectAnalysis.backToProject')}
            </DetailButton>
            <AnalysisPdfButton
              exporting={pdf.exporting}
              disabled={loading}
              onExport={() =>
                pdf.exportPdf(projet?.ProjetName || 'project-analysis')
              }
            />
          </>
        }
      />

      {loading ? (
        <AnalysisPageLoader kpiCount={3} chartCount={4} />
      ) : (
        <>
      <div className="ma-kpi-grid ma-kpi-grid--3">
        <AnalysisKpiCard
          label={t('charts.taskStatus.overdue')}
          value={formatDecimal(overduePct)}
          suffix="%"
          tone={pctTone(overduePct)}
          progress={overduePct}
          stampTitle={stampTitle}
          description={t('analysis.projectAnalysis.overdueDesc')}
        />
        <AnalysisKpiCard
          label={t('charts.priority.overflowed')}
          value={formatDecimal(highPriorityPct)}
          suffix="%"
          tone={pctTone(highPriorityPct)}
          progress={highPriorityPct}
          stampTitle={stampTitle}
          description={t('analysis.projectAnalysis.highPriorityDesc')}
        />
        <AnalysisKpiCard
          label={t('charts.project.budgetPercentage')}
          value={formatDecimal(budgetPct)}
          suffix="%"
          tone={pctTone(budgetPct)}
          progress={budgetPct}
          stampTitle={stampTitle}
          description={t('analysis.projectAnalysis.budgetDesc')}
        />
      </div>

      <AnalysisSection label={t('analysis.projectAnalysis.sectionCharts')}>
        <AnalysisChartCard
          title={t('charts.taskStatus.byStatus')}
          hasData={hasChartData(tasksByStatusData)}
        >
          <PieChart
            embedded
            title={t('charts.taskStatus.byStatus')}
            chartTitle={t('charts.taskStatus.byStatus')}
            data={tasksByStatusData}
            itemRow={3}
          />
        </AnalysisChartCard>
        <AnalysisChartCard
          title={t('charts.taskPriority.byPriority')}
          hasData={hasChartData(tasksByPriorityData)}
        >
          <PieChart
            embedded
            title={t('charts.taskPriority.byPriority')}
            chartTitle={t('charts.taskPriority.byPriority')}
            data={tasksByPriorityData}
            itemRow={3}
          />
        </AnalysisChartCard>
        <AnalysisChartCard
          title={t('charts.budget.comparison')}
          hasData={hasChartData(budgetComparisonData)}
        >
          <PieChart
            embedded
            title={t('charts.budget.comparison')}
            chartTitle={t('charts.budget.comparison')}
            data={budgetComparisonData}
            itemRow={3}
          />
        </AnalysisChartCard>
        <AnalysisChartCard
          title={t('charts.actions.bySource')}
          hasData={hasChartData(actionsBySourceData)}
        >
          <BarChart
            embedded
            title={t('charts.actions.bySource')}
            chartTitle={t('charts.actions.bySource')}
            data={actionsBySourceData}
          />
        </AnalysisChartCard>
        <AnalysisChartCard
          title={t('charts.actions.byType')}
          hasData={hasChartData(actionsByTypeData)}
        >
          <BarChart
            embedded
            title={t('charts.actions.byType')}
            chartTitle={t('charts.actions.byType')}
            data={actionsByTypeData}
          />
        </AnalysisChartCard>
        <AnalysisChartCard
          title={t('charts.actionProgressBySource.title')}
          hasData={hasChartData(stackedBarChartData)}
        >
          <BarChart
            embedded
            title={t('charts.actionProgressBySource.title')}
            chartTitle={t('charts.actionProgressBySource.title')}
            data={stackedBarChartData}
          />
        </AnalysisChartCard>
        <AnalysisChartCard
          title={t('charts.securityCheck.byProject')}
          hasData={hasChartData(SecurityData)}
        >
          <BarChart
            embedded
            title={t('charts.securityCheck.byProject')}
            chartTitle={t('charts.securityCheck.byProject')}
            data={SecurityData}
          />
        </AnalysisChartCard>
        <AnalysisChartCard
          title={t('charts.scfChart.title')}
          hasData={hasChartData(chartData)}
        >
          <LineChart
            embedded
            title={t('charts.scfChart.title')}
            data={chartData}
          />
        </AnalysisChartCard>
      </AnalysisSection>

      <p className="ma-section-label">
        {t('analysis.projectAnalysis.sectionBudget')}
      </p>
      <div className="ma-card">
        <div className="ma-card-head">
          <h3>{t('charts.taskBudgetMatrix.title')}</h3>
          <span className="ma-card-hint">
            {t('analysis.projectAnalysis.budgetTableHint', {
              count: formattedData.length,
            })}
          </span>
        </div>
        <div className="ma-card-body ma-card-body--table">
          <MatrixComponent
            variant="analysis"
            title={t('charts.taskBudgetMatrix.title')}
            headers={headers}
            data={formattedData}
          />
        </div>
      </div>
        </>
      )}

      <div className="ma-pdf-print-host" aria-hidden>
        <AnalysisPdfReport
          ref={pdf.ref}
          eyebrow={t('analysis.projectAnalysis.eyebrow')}
          title={projet?.ProjetName || '—'}
          subtitle={t('analysis.projectAnalysis.subtitle', {
            name: projet?.ProjetName || '—',
            date: computedOn,
          })}
          footerTitle={t('analysis.projectAnalysis.pageTitle')}
          computedOn={computedOn}
          kpis={[
            {
              label: t('charts.taskStatus.overdue'),
              value: `${formatDecimal(overduePct)} %`,
              desc: t('analysis.projectAnalysis.overdueDesc'),
              tone: pctTone(overduePct),
              progress: overduePct,
            },
            {
              label: t('charts.priority.overflowed'),
              value: `${formatDecimal(highPriorityPct)} %`,
              desc: t('analysis.projectAnalysis.highPriorityDesc'),
              tone: pctTone(highPriorityPct),
              progress: highPriorityPct,
            },
            {
              label: t('charts.project.budgetPercentage'),
              value: `${formatDecimal(budgetPct)} %`,
              desc: t('analysis.projectAnalysis.budgetDesc'),
              tone: pctTone(budgetPct),
              progress: budgetPct,
            },
          ]}
          sections={[
            {
              label: t('analysis.projectAnalysis.sectionCharts'),
              charts: [
                { key: 'status', title: t('charts.taskStatus.byStatus'), kind: 'pie', data: tasksByStatusData },
                { key: 'priority', title: t('charts.taskPriority.byPriority'), kind: 'pie', data: tasksByPriorityData },
                { key: 'budget', title: t('charts.budget.comparison'), kind: 'pie', data: budgetComparisonData },
                { key: 'source', title: t('charts.actions.bySource'), kind: 'bar', data: actionsBySourceData },
                { key: 'type', title: t('charts.actions.byType'), kind: 'bar', data: actionsByTypeData },
                { key: 'progress', title: t('charts.actionProgressBySource.title'), kind: 'bar', data: stackedBarChartData },
                { key: 'security', title: t('charts.securityCheck.byProject'), kind: 'bar', data: SecurityData },
                { key: 'scf', title: t('charts.scfChart.title'), kind: 'line', data: chartData },
              ],
            },
          ]}
          tables={[
            {
              label: t('analysis.projectAnalysis.sectionBudget'),
              title: t('charts.taskBudgetMatrix.title'),
              hint: t('analysis.projectAnalysis.budgetTableHint', {
                count: TaskBudget.length,
              }),
              headers,
              rows: TaskBudget.map((row) => [row.name, row.budget]),
              numericLast: true,
            },
          ]}
        />
      </div>
    </div>
  )
}

export default ProjectIndicatorsPage
